import React, { useState, useEffect } from 'react';
import { transactionAPI } from '../services/api';
import { 
  ArrowDownTrayIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  QrCodeIcon
} from '@heroicons/react/24/outline';

const Deposit = () => {
  const [amount, setAmount] = useState('');
  const [selectedCrypto, setSelectedCrypto] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [senderAddress, setSenderAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [depositMethods, setDepositMethods] = useState([]);
  const [showCryptoDetails, setShowCryptoDetails] = useState(false);
  const [copiedField, setCopiedField] = useState('');
  const [currentDeposit, setCurrentDeposit] = useState(null);
  const [step, setStep] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    fetchDepositMethods();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchDepositMethods = async () => {
    try {
      const response = await transactionAPI.getDepositMethods();
      if (response.data && response.data.methods) {
        setDepositMethods(response.data.methods);
        if (response.data.methods.length > 0) {
          setSelectedCrypto(response.data.methods[0].type);
        }
      }
    } catch (error) {
      console.error('Error fetching deposit methods:', error);
      alert('Failed to load deposit methods. Please refresh the page.');
    }
  };

  const quickAmounts = [50, 100, 250, 500, 1000, 2000];

  const handleDeposit = async () => {
    if (!amount || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (!selectedCrypto) {
      alert('Please select a cryptocurrency');
      return;
    }

    if (!transactionId || transactionId.trim() === '') {
      alert('Transaction ID is required');
      return;
    }

    const selectedCryptoMethod = depositMethods.find(m => m.type === selectedCrypto);
    const minDeposit = selectedCryptoMethod?.minDeposit || 50;

    if (amount < minDeposit) {
      alert(`Minimum deposit for ${selectedCrypto} is $${minDeposit}`);
      return;
    }

    try {
      setLoading(true);
      const response = await transactionAPI.deposit({
        amount: Number(amount),
        cryptoType: selectedCrypto,
        transactionId: transactionId.trim(),
        senderAddress: senderAddress.trim() || 'Not provided'
      });
      
      if (response.data && response.data.deposit) {
        setCurrentDeposit(response.data.deposit);
        setStep(3);
      }
      setTransactionId('');
      setSenderAddress('');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to submit deposit request');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(''), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedField(field);
      setTimeout(() => setCopiedField(''), 2000);
    }
  };

  const getSelectedCryptoDetails = () => {
    return depositMethods.find(m => m.type === selectedCrypto);
  };

  const getCryptoDisplayName = () => {
    const crypto = getSelectedCryptoDetails();
    return crypto ? crypto.name : selectedCrypto;
  };

  const cryptoDetails = getSelectedCryptoDetails();

  return (
    <div style={{
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
      paddingBottom: isMobile ? '80px' : '20px',
      backgroundColor: '#0a0a0a',
      minHeight: '100vh'
    }}>
      {/* Header - Dark Theme */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
        borderRadius: '20px',
        padding: '24px',
        color: 'white',
        marginBottom: '24px',
        boxShadow: '0 10px 30px rgba(0, 212, 255, 0.2)',
        border: '1px solid rgba(0, 212, 255, 0.3)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 0 8px 0',
              background: 'linear-gradient(135deg, #fff, #00d4ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>Deposit Funds</h1>
            <p style={{
              opacity: '0.7',
              margin: 0,
              fontSize: '14px'
            }}>Add crypto to your account</p>
          </div>
          <ArrowDownTrayIcon style={{ width: '32px', height: '32px', color: '#00d4ff' }} />
        </div>
        
        <div style={{
          background: 'rgba(0, 212, 255, 0.1)',
          borderRadius: '16px',
          padding: '16px',
          border: '1px solid rgba(0, 212, 255, 0.2)'
        }}>
          <p style={{
            fontSize: '14px',
            opacity: '0.7',
            margin: '0 0 8px 0'
          }}>Minimum Deposit</p>
          <p style={{
            fontSize: '28px',
            fontWeight: '700',
            margin: 0,
            color: '#00d4ff'
          }}>${cryptoDetails?.minDeposit || 50}</p>
          <p style={{
            fontSize: '12px',
            opacity: '0.7',
            margin: '8px 0 0 0'
          }}>Processed in 1-30 minutes</p>
        </div>
      </div>

      {/* Progress Steps - Dark Theme */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px',
        background: '#1a1a2e',
        borderRadius: '20px',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        marginBottom: '24px'
      }}>
        {[1, 2, 3].map((num) => (
          <div key={num} style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            flex: 1
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: step >= num ? '#00d4ff' : 'rgba(255, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '600',
              color: step >= num ? '#1a1a2e' : '#94a3b8',
              boxShadow: step >= num ? '0 0 10px rgba(0, 212, 255, 0.5)' : 'none'
            }}>
              {num}
            </div>
            <span style={{
              fontSize: '12px',
              color: step >= num ? '#00d4ff' : '#64748b',
              fontWeight: step >= num ? '600' : '400'
            }}>
              {num === 1 ? 'Select Amount' : num === 2 ? 'Enter Details' : 'Confirm'}
            </span>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Step 1: Amount Selection - Dark Theme */}
        {step === 1 && (
          <div style={{
            background: '#1a1a2e',
            borderRadius: '24px',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            padding: '24px'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#00d4ff',
              marginBottom: '20px'
            }}>Select Deposit Amount</h2>
            
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <CurrencyDollarIcon style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '20px',
                height: '20px',
                color: '#00d4ff'
              }} />
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                style={{
                  width: '100%',
                  padding: '16px 80px 16px 48px',
                  fontSize: '24px',
                  fontWeight: '700',
                  color: 'white',
                  background: '#0f0f0f',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  borderRadius: '16px',
                  outline: 'none'
                }}
                min={cryptoDetails?.minDeposit || 50}
              />
              <button 
                onClick={() => setAmount('1000')}
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: '#00d4ff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  color: '#1a1a2e',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                $1000
              </button>
            </div>
            
            <p style={{
              fontSize: '14px',
              color: '#94a3b8',
              marginBottom: '20px'
            }}>
              Minimum: <span style={{ color: '#00d4ff', fontWeight: '600' }}>${cryptoDetails?.minDeposit || 50}</span>
            </p>
            
            {/* Quick Amounts */}
            <div style={{ marginBottom: '24px' }}>
              <p style={{
                fontSize: '14px',
                color: '#94a3b8',
                marginBottom: '12px'
              }}>Quick Select</p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
                gap: '8px'
              }}>
                {quickAmounts.map(amt => (
                  <button
                    key={amt}
                    onClick={() => setAmount(amt.toString())}
                    style={{
                      padding: '12px',
                      background: Number(amount) === amt ? '#00d4ff' : '#0f0f0f',
                      border: '1px solid rgba(0, 212, 255, 0.3)',
                      borderRadius: '12px',
                      color: Number(amount) === amt ? '#1a1a2e' : '#94a3b8',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    ${amt}
                  </button>
                ))}
              </div>
            </div>

            {/* Crypto Selection */}
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#00d4ff',
              marginBottom: '20px'
            }}>Select Cryptocurrency</h2>
            <div style={{
              display: 'grid',
              gap: '12px',
              marginBottom: '24px'
            }}>
              {depositMethods.map((crypto) => (
                <button
                  key={crypto.type}
                  onClick={() => setSelectedCrypto(crypto.type)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px',
                    background: selectedCrypto === crypto.type ? 'rgba(0, 212, 255, 0.15)' : '#0f0f0f',
                    border: selectedCrypto === crypto.type ? '1px solid #00d4ff' : '1px solid rgba(0, 212, 255, 0.2)',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    textAlign: 'left'
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: '#00d4ff',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    flexShrink: 0
                  }}>
                    <span style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: '#1a1a2e'
                    }}>{crypto.symbol || crypto.name.charAt(0)}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: 'white',
                      marginBottom: '4px'
                    }}>{crypto.name}</h4>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                      <span style={{
                        background: 'rgba(0, 212, 255, 0.2)',
                        color: '#00d4ff',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: '600'
                      }}>{crypto.network}</span>
                      {crypto.memoRequired && (
                        <span style={{
                          background: 'rgba(245, 158, 11, 0.2)',
                          color: '#f59e0b',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}>Memo Required</span>
                      )}
                    </div>
                    <p style={{
                      fontSize: '12px',
                      color: '#94a3b8',
                      margin: 0
                    }}>Min: ${crypto.minDeposit}</p>
                  </div>
                  {selectedCrypto === crypto.type && (
                    <CheckCircleIcon style={{ width: '20px', height: '20px', color: '#00d4ff' }} />
                  )}
                </button>
              ))}
            </div>

            <button
              onClick={() => setStep(2)}
              disabled={!amount || !selectedCrypto}
              style={{
                width: '100%',
                padding: '16px',
                background: (!amount || !selectedCrypto) ? '#334155' : '#00d4ff',
                border: 'none',
                borderRadius: '16px',
                color: (!amount || !selectedCrypto) ? '#94a3b8' : '#1a1a2e',
                fontSize: '16px',
                fontWeight: '600',
                cursor: (!amount || !selectedCrypto) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Continue
            </button>
          </div>
        )}

        {/* Step 2: Deposit Details - Dark Theme */}
        {step === 2 && (
          <>
            {/* Deposit Address Card */}
            <div style={{
              background: '#1a1a2e',
              borderRadius: '24px',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              padding: '24px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#00d4ff',
                  margin: 0
                }}>Deposit Address</h2>
                <button 
                  onClick={() => setShowCryptoDetails(!showCryptoDetails)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: showCryptoDetails ? '#00d4ff' : '#94a3b8'
                  }}
                >
                  <InformationCircleIcon style={{ width: '20px', height: '20px' }} />
                </button>
              </div>
              
              {cryptoDetails && (
                <>
                  <div style={{
                    background: 'rgba(0, 212, 255, 0.05)',
                    borderRadius: '20px',
                    padding: '20px',
                    border: '1px solid rgba(0, 212, 255, 0.2)',
                    marginBottom: '20px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      marginBottom: '20px'
                    }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        background: '#00d4ff',
                        borderRadius: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <span style={{
                          fontSize: '24px',
                          fontWeight: '700',
                          color: '#1a1a2e'
                        }}>{cryptoDetails.symbol || cryptoDetails.name.charAt(0)}</span>
                      </div>
                      <div>
                        <h3 style={{
                          fontSize: '20px',
                          fontWeight: '700',
                          color: 'white',
                          marginBottom: '4px'
                        }}>{cryptoDetails.name}</h3>
                        <p style={{
                          fontSize: '14px',
                          color: '#94a3b8',
                          margin: 0
                        }}>{cryptoDetails.network}</p>
                      </div>
                    </div>
                    
                    {/* Address Display */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        color: '#e2e8f0',
                        marginBottom: '8px',
                        fontWeight: '500'
                      }}>Send {cryptoDetails.name} to:</label>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        background: '#0f0f0f',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        border: '1px solid rgba(0, 212, 255, 0.3)'
                      }}>
                        <code style={{
                          flex: 1,
                          fontFamily: 'monospace',
                          fontSize: '13px',
                          color: 'white',
                          wordBreak: 'break-all'
                        }}>
                          {cryptoDetails.address}
                        </code>
                        <button
                          onClick={() => copyToClipboard(cryptoDetails.address, 'address')}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 16px',
                            background: copiedField === 'address' ? '#00d4ff' : 'rgba(0, 212, 255, 0.2)',
                            border: 'none',
                            borderRadius: '8px',
                            color: copiedField === 'address' ? '#1a1a2e' : '#00d4ff',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            transition: 'all 0.2s'
                          }}
                        >
                          <DocumentDuplicateIcon style={{ width: '16px', height: '16px' }} />
                          {copiedField === 'address' ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </div>

                    {/* Memo (if required) */}
                    {cryptoDetails.memoRequired && (
                      <div style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        borderRadius: '16px',
                        padding: '16px',
                        marginBottom: '20px',
                        border: '1px solid rgba(239, 68, 68, 0.3)'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '12px'
                        }}>
                          <ExclamationTriangleIcon style={{ width: '18px', height: '18px', color: '#f87171' }} />
                          <label style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#f87171'
                          }}>Memo / Tag (REQUIRED)</label>
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          background: '#0f0f0f',
                          borderRadius: '12px',
                          padding: '12px 16px',
                          marginBottom: '8px'
                        }}>
                          <code style={{
                            flex: 1,
                            fontFamily: 'monospace',
                            fontSize: '14px',
                            color: '#f87171',
                            fontWeight: '600'
                          }}>
                            {cryptoDetails.memo}
                          </code>
                          <button
                            onClick={() => copyToClipboard(cryptoDetails.memo, 'memo')}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '8px 16px',
                              background: copiedField === 'memo' ? '#00d4ff' : 'rgba(239, 68, 68, 0.2)',
                              border: 'none',
                              borderRadius: '8px',
                              color: copiedField === 'memo' ? '#1a1a2e' : '#f87171',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            <DocumentDuplicateIcon style={{ width: '16px', height: '16px' }} />
                            {copiedField === 'memo' ? 'Copied!' : 'Copy'}
                          </button>
                        </div>
                        <p style={{
                          fontSize: '12px',
                          color: '#f87171',
                          margin: 0
                        }}>
                          ⚠️ Funds will be lost without correct memo
                        </p>
                      </div>
                    )}

                    {/* Copy All Button */}
                    <button
                      onClick={() => copyToClipboard(
                        cryptoDetails.memoRequired 
                          ? `Address: ${cryptoDetails.address}\nMemo: ${cryptoDetails.memo}`
                          : cryptoDetails.address, 
                        'all'
                      )}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: '#00d4ff',
                        border: 'none',
                        borderRadius: '12px',
                        color: '#1a1a2e',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <DocumentDuplicateIcon style={{ width: '18px', height: '18px' }} />
                      Copy All Details
                    </button>
                  </div>

                  {/* Expanded Details - Dark Theme */}
                  {showCryptoDetails && (
                    <div style={{
                      marginTop: '20px',
                      paddingTop: '20px',
                      borderTop: '1px solid rgba(0, 212, 255, 0.2)'
                    }}>
                      {/* Instructions */}
                      <div style={{
                        background: '#0f0f0f',
                        padding: '16px',
                        borderRadius: '12px',
                        marginBottom: '20px'
                      }}>
                        <h4 style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#00d4ff',
                          marginBottom: '12px'
                        }}>How to Deposit:</h4>
                        <ol style={{
                          margin: 0,
                          paddingLeft: '20px',
                          color: '#94a3b8',
                          fontSize: '14px',
                          lineHeight: '1.8'
                        }}>
                          <li>Copy the {cryptoDetails.name} address above</li>
                          <li>Open your cryptocurrency wallet</li>
                          <li>Paste the address in recipient field</li>
                          {cryptoDetails.memoRequired && (
                            <li><strong>Paste the memo in memo/tag field</strong></li>
                          )}
                          <li>Enter amount: <strong style={{ color: '#00d4ff' }}>${amount}</strong></li>
                          <li>Confirm and send transaction</li>
                        </ol>
                      </div>

                      {/* QR Code */}
                      <div style={{
                        textAlign: 'center',
                        marginBottom: '20px'
                      }}>
                        <h4 style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#00d4ff',
                          marginBottom: '12px'
                        }}>QR Code</h4>
                        <div style={{
                          background: 'white',
                          padding: '20px',
                          borderRadius: '12px',
                          display: 'inline-block'
                        }}>
                          <img 
                            src={cryptoDetails.qrCode} 
                            alt={`${cryptoDetails.name} Address`}
                            style={{ width: '150px', height: '150px', cursor: 'pointer' }}
                            onClick={() => window.open(cryptoDetails.qrCode, '_blank')}
                          />
                        </div>
                      </div>

                      {/* Network Warning */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        background: 'rgba(245, 158, 11, 0.1)',
                        borderRadius: '12px',
                        padding: '16px',
                        border: '1px solid rgba(245, 158, 11, 0.3)'
                      }}>
                        <ShieldCheckIcon style={{ width: '20px', height: '20px', color: '#f59e0b' }} />
                        <div>
                          <h4 style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#f59e0b',
                            marginBottom: '4px'
                          }}>Important Warning</h4>
                          <p style={{
                            fontSize: '13px',
                            color: '#fbbf24',
                            margin: 0
                          }}>
                            Send only {cryptoDetails.name} on {cryptoDetails.network} network.
                            Wrong network transfers result in lost funds.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Transaction Details Form - Dark Theme */}
            <div style={{
              background: '#1a1a2e',
              borderRadius: '24px',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              padding: '24px'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#00d4ff',
                marginBottom: '20px'
              }}>Transaction Details</h2>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#e2e8f0',
                  marginBottom: '8px'
                }}>
                  Transaction ID / TX Hash *
                </label>
                <input
                  type="text"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  placeholder="e.g., 0x1234...abcd or txid123..."
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: '#0f0f0f',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  required
                />
                <p style={{
                  fontSize: '12px',
                  color: '#64748b',
                  marginTop: '4px'
                }}>
                  Required for verification. Find in your wallet's transaction history.
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#e2e8f0',
                  marginBottom: '8px'
                }}>
                  Your Wallet Address (Optional)
                </label>
                <input
                  type="text"
                  value={senderAddress}
                  onChange={(e) => setSenderAddress(e.target.value)}
                  placeholder="Your sending wallet address"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: '#0f0f0f',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                background: 'rgba(0, 212, 255, 0.1)',
                padding: '12px 16px',
                borderRadius: '12px'
              }}>
                <ClockIcon style={{ width: '18px', height: '18px', color: '#00d4ff' }} />
                <span style={{
                  fontSize: '13px',
                  color: '#00d4ff'
                }}>
                  Deposits require 1-30 confirmations. You can track on blockchain explorer.
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={() => setStep(1)}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: '#0f0f0f',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  borderRadius: '16px',
                  color: '#94a3b8',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Back
              </button>
              <button
                onClick={handleDeposit}
                disabled={loading || !transactionId}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: (loading || !transactionId) ? '#334155' : '#00d4ff',
                  border: 'none',
                  borderRadius: '16px',
                  color: (loading || !transactionId) ? '#94a3b8' : '#1a1a2e',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: (loading || !transactionId) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {loading ? (
                  <>
                    <ArrowPathIcon style={{ width: '18px', height: '18px', animation: 'spin 1s linear infinite' }} />
                    Processing...
                  </>
                ) : `Confirm Deposit $${amount}`}
              </button>
            </div>
          </>
        )}

        {/* Step 3: Active Deposit - Dark Theme */}
        {step === 3 && currentDeposit && (
          <div style={{
            background: 'rgba(0, 212, 255, 0.1)',
            borderRadius: '24px',
            border: '2px solid #00d4ff',
            padding: '24px',
            animation: 'slideUp 0.3s ease-out'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <CheckCircleIcon style={{ width: '48px', height: '48px', color: '#00d4ff', marginBottom: '16px' }} />
              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: 'white',
                marginBottom: '8px'
              }}>Deposit Request Created!</h2>
              <p style={{ color: '#94a3b8' }}>Send {currentDeposit.cryptoType} within 30 minutes</p>
            </div>

            <div style={{
              background: '#0f0f0f',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '24px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <span style={{ color: '#94a3b8' }}>Amount:</span>
                <span style={{ color: '#00d4ff', fontWeight: '600' }}>${currentDeposit.amount}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <span style={{ color: '#94a3b8' }}>Cryptocurrency:</span>
                <span style={{ color: 'white', fontWeight: '600' }}>{currentDeposit.cryptoType}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <span style={{ color: '#94a3b8' }}>Network:</span>
                <span style={{ color: 'white', fontWeight: '600' }}>{currentDeposit.network}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 0'
              }}>
                <span style={{ color: '#94a3b8' }}>Expires in:</span>
                <span style={{ color: '#f59e0b', fontWeight: '600' }}>
                  {Math.floor((new Date(currentDeposit.expiresAt) - new Date()) / (1000 * 60))} minutes
                </span>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                color: '#e2e8f0',
                marginBottom: '8px'
              }}>Send to this address:</label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                background: '#0f0f0f',
                borderRadius: '12px',
                padding: '12px 16px'
              }}>
                <code style={{
                  flex: 1,
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  color: 'white',
                  wordBreak: 'break-all'
                }}>{currentDeposit.cryptoAddress}</code>
                <button
                  onClick={() => copyToClipboard(currentDeposit.cryptoAddress, 'deposit-address')}
                  style={{
                    padding: '8px 16px',
                    background: 'rgba(0, 212, 255, 0.2)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#00d4ff',
                    cursor: 'pointer'
                  }}
                >
                  <DocumentDuplicateIcon style={{ width: '16px', height: '16px' }} />
                </button>
              </div>
              {currentDeposit.memo !== 'N/A' && (
                <div style={{
                  marginTop: '12px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  borderRadius: '12px',
                  padding: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <ExclamationTriangleIcon style={{ width: '16px', height: '16px', color: '#f87171' }} />
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#f87171' }}>Memo Required:</label>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <code style={{
                      flex: 1,
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      color: '#f87171'
                    }}>{currentDeposit.memo}</code>
                    <button
                      onClick={() => copyToClipboard(currentDeposit.memo, 'deposit-memo')}
                      style={{
                        padding: '6px 12px',
                        background: 'rgba(239, 68, 68, 0.2)',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#f87171',
                        cursor: 'pointer'
                      }}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              marginBottom: '24px'
            }}>
              <button
                onClick={() => window.open(currentDeposit.qrCode, '_blank')}
                style={{
                  padding: '12px',
                  background: '#0f0f0f',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  borderRadius: '12px',
                  color: '#00d4ff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <QrCodeIcon style={{ width: '18px', height: '18px' }} />
                View QR Code
              </button>
              <button
                onClick={() => window.open(`https://explorer.com/tx/${currentDeposit.txHash || 'search'}`, '_blank')}
                style={{
                  padding: '12px',
                  background: '#0f0f0f',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  borderRadius: '12px',
                  color: '#00d4ff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <InformationCircleIcon style={{ width: '18px', height: '18px' }} />
                Track Transaction
              </button>
            </div>

            <button
              onClick={() => {
                setCurrentDeposit(null);
                setStep(1);
                setAmount('');
                setTransactionId('');
              }}
              style={{
                width: '100%',
                padding: '16px',
                background: '#00d4ff',
                border: 'none',
                borderRadius: '16px',
                color: '#1a1a2e',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Done
            </button>
          </div>
        )}

        {/* Important Info - Dark Theme */}
        <div style={{
          background: '#1a1a2e',
          borderRadius: '24px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          padding: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <ShieldCheckIcon style={{ width: '24px', height: '24px', color: '#00d4ff' }} />
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#00d4ff',
              margin: 0
            }}>Important Information</h3>
          </div>
          <ul style={{
            margin: 0,
            paddingLeft: '20px',
            color: '#94a3b8',
            fontSize: '14px',
            lineHeight: '1.8'
          }}>
            <li>Send only {getCryptoDisplayName()} to the provided address</li>
            <li>Double-check the network before sending</li>
            {cryptoDetails?.memoRequired && (
              <li style={{ color: '#f87171' }}>Memo is REQUIRED - Funds lost without correct memo</li>
            )}
            <li>Send exact amount: <strong style={{ color: '#00d4ff' }}>${amount || '___'}</strong></li>
            <li>Deposit request expires in 30 minutes</li>
            <li>Processing time: 1-30 confirmations</li>
            <li>You pay network/gas fees</li>
          </ul>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        body {
          background-color: #0a0a0a;
        }
      `}</style>
    </div>
  );
};

export default Deposit;